/**
 * Pure per-row evaluator for FilterGroup[]. Used to gate loose-FK badge
 * visibility + popover entries when a loose FK has a sourceFilter — the
 * filter is evaluated against the current row data (already in hand)
 * without an SQL round-trip.
 *
 * Semantics mirror filterWhereBuilder.buildFilterWhereClause:
 *   - Conditions within a group are OR-ed.
 *   - Groups are AND-ed.
 *   - No filter groups = match everything.
 *
 * LIKE uses SQL-style % and _ wildcards; CONTAINS matches the value
 * literally, as the emitted SQL does (`matchesContains`).
 */

import type { FilterCondition, FilterGroup } from '../../core/types/workspaceTypes';
import { extractJsonPathScalar, extractJsonPathRaw } from './extractJsonPath';
import { compareOrdered, compareBetween, matchesContains } from './filterCompare';

function likeToRegExp(pattern: string): RegExp {
  // Escape regex specials, then replace SQL wildcards.
  // NOTE: no implicit `%…%` wrapping — unlike the Data Compare grid filter,
  // this evaluator stands in for a real SQL WHERE, where `LIKE 'Adventure'`
  // does not match 'AdventureWorks'. See filterCompare.matchesLikePattern.
  const escaped = pattern
    .replace(/[.+^${}()|[\]\\]/g, '\\$&')
    .replace(/%/g, '.*')
    .replace(/_/g, '.');
  return new RegExp(`^${escaped}$`, 'i');
}

function evaluateCondition(rowValue: unknown, c: FilterCondition): boolean {
  switch (c.operator) {
    case 'IS NULL':
      return rowValue === null || rowValue === undefined;
    case 'IS NOT NULL':
      return rowValue !== null && rowValue !== undefined;
    // `=` / `!=` stay case-SENSITIVE here (they mirror a SQL WHERE on PG /
    // Oracle / SQLite), while the Data Compare grid filter is case-insensitive.
    // Deliberate — see the header of ./filterCompare.ts.
    case '=':
    case 'lookup':
      return rowValue === c.value || String(rowValue) === String(c.value);
    case 'enum':
      if (Array.isArray(c.value)) {
        // `c.value` is `(string | number)[]` (decision #4) — String()
        // normalizes both a numeric array member and a numeric/string cell
        // value onto the same representation before comparing, mirroring
        // the scalar branch below.
        return c.value.map(String).includes(String(rowValue));
      }
      return rowValue === c.value || String(rowValue) === String(c.value);
    case '!=':
      return !(rowValue === c.value || String(rowValue) === String(c.value));
    // "in list" / "not in list": membership by the same String() comparison as
    // `enum`. A NULL cell never matches NOT IN (SQL's `NULL NOT IN (…)` is
    // unknown). An empty list never gets here (`isEmptyList`).
    case 'IN':
    case 'NOT IN': {
      if (!Array.isArray(c.value) || rowValue === null || rowValue === undefined) return false;
      const inList = c.value.map(String).includes(String(rowValue));
      return c.operator === 'IN' ? inList : !inList;
    }
    // B10 / F02-P4: these were numeric-only and returned `false` for every
    // non-numeric operand, so a date or text range silently matched nothing
    // here while the identical FilterGroup matched correctly in Data Compare.
    // Both now share ./filterCompare.ts (numeric → date → lexicographic), and
    // SQL orders dates and text, so this is closer to this evaluator's own
    // "mirror buildFilterWhereClause" contract than the old gap was.
    case '<':
    case '<=':
    case '>':
    case '>=':
      return compareOrdered(rowValue, c.value, c.operator);
    case 'BETWEEN':
      return compareBetween(rowValue, c.value, c.value2);
    case 'LIKE': {
      if (rowValue === null || rowValue === undefined) return false;
      return likeToRegExp(String(c.value ?? '')).test(String(rowValue));
    }
    case 'CONTAINS':
      return matchesContains(rowValue, c.value);
    // Array operators: source-filter is evaluated client-side against row
    // data. For the foreseeable use case (polymorphic discriminator on a
    // scalar column), array ops don't apply. Fall back to "no match" so
    // a misconfigured filter doesn't accidentally enable the FK.
    case 'array_contains':
    case 'array_not_contains':
    case 'array_is_empty':
    case 'array_is_not_empty':
    case 'array_length_eq':
    case 'array_length_neq':
    case 'array_length_gt':
    case 'array_length_lt':
    case 'array_length_gte':
    case 'array_length_lte':
      return false;
    case 'json': {
      const sub = c.jsonSubOperator;
      const path = c.path ?? '';
      // Empty multiselect value matches nothing — mirrors the SQL `1 = 0`
      // short-circuit in buildJsonConditionSql (Task 8).
      if (Array.isArray(c.value) && c.value.length === 0) return false;
      // property_enum: scalar at path. value array → membership; scalar →
      // equality. A missing path OR a JSON null both extract to SQL NULL,
      // whose comparison is never true — return false to match the SQL emit.
      if (sub === 'property_enum') {
        const extracted = extractJsonPathScalar(rowValue, path);
        if (extracted === undefined || extracted === null) return false;
        if (Array.isArray(c.value)) {
          return (c.value as string[]).map(String).includes(String(extracted));
        }
        return String(extracted) === String(c.value);
      }
      // any_enum: path resolves to an array; true if SOME non-null element
      // (stringified) is in value (array) or equals value (scalar). A JSON
      // null element never matches in SQL (NULL = x / NULL IN (...) is NULL).
      if (sub === 'any_enum') {
        const arr = extractJsonPathRaw(rowValue, path);
        if (!Array.isArray(arr) || arr.length === 0) return false;
        const elems = (arr as unknown[])
          .filter((el) => el !== null && el !== undefined)
          .map(String);
        if (elems.length === 0) return false;
        if (Array.isArray(c.value)) {
          const set = new Set((c.value as string[]).map(String));
          return elems.some((el) => set.has(el));
        }
        return elems.some((el) => el === String(c.value));
      }
      // all_enum: path resolves to a NON-EMPTY array; true if EVERY element
      // (stringified) is in value (array) or equals value (scalar). A JSON
      // null element is a violation in SQL (elementExpr IS NULL OR …), so any
      // null/undefined element makes the row fail.
      if (sub === 'all_enum') {
        const arr = extractJsonPathRaw(rowValue, path);
        if (!Array.isArray(arr) || arr.length === 0) return false;
        const raw = arr as unknown[];
        if (raw.some((el) => el === null || el === undefined)) return false;
        const elems = raw.map(String);
        if (Array.isArray(c.value)) {
          const set = new Set((c.value as string[]).map(String));
          return elems.every((el) => set.has(el));
        }
        return elems.every((el) => el === String(c.value));
      }
      // All other json sub-operators (lookup trio, path_eq, has_property, …)
      // are not evaluated in-memory — fall through to false (unchanged
      // pre-existing behavior).
      return false;
    }
    default:
      return false;
  }
}

export function evaluateFilterGroupsAgainstRow(
  row: Record<string, unknown>,
  groups: FilterGroup[] | undefined,
): boolean {
  if (!groups || groups.length === 0) return true;
  return groups.every((g) => {
    // An empty "in list" writes no SQL predicate, so it is not part of the group.
    const conditions = g.conditions.filter((c) => !isEmptyList(c));
    if (conditions.length === 0) return true;
    if (!(g.field in row)) return false;
    return conditions.some((c) => evaluateCondition(row[g.field], c));
  });
}

/** An "in list" / "not in list" condition with no values — inactive, like its SQL. */
function isEmptyList(c: FilterCondition): boolean {
  return (c.operator === 'IN' || c.operator === 'NOT IN') && !(Array.isArray(c.value) && c.value.length > 0);
}
